import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const useStyles = makeStyles(() => ({
  dialogPaper: {
    borderRadius: 18,
    background: '#f7faff',
    border: '1px solid rgba(0,45,85,0.08)',
    boxShadow: '0 18px 44px rgba(0,45,85,0.16)',
  },
  trendTooltip: {
    backgroundColor: '#f7faff',
    color: '#002D55',
    border: '1px solid #dce8f5',
    borderRadius: 16,
    boxShadow: '0 14px 32px rgba(0,45,85,0.18)',
    padding: '12px 14px',
    maxWidth: 360,
  },
  trendTooltipArrow: {
    color: '#f7faff',
  },
  footerButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 50,
    border: '1px solid transparent',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 5px) clamp(7px, 0.85vw, 15px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '1px solid #FF7F27',
      boxShadow: 'none',
    },
  },
}));

const EXERCISE_TREND_SESSION_LIMIT = 8;
const EXERCISE_TREND_SESSION_STEP = 3;
const PAIN_TREND_STABLE_DELTA = 2;

const DEFAULT_EXERCISE_TREND_SUMMARY = {
  sessions: [],
  sessionGroups: [],
  exercises: [],
  overall: {
    averagePain: null,
    averageCompliance: null,
    painTrend: { direction: 'unknown', delta: null },
  },
};

const defaultNormalizeExerciseKey = (value) => (value || '')
  .toString()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '');

const defaultGetDifficultyDisplayLabel = (difficulty) => difficulty ?? '-';

const clampTrendValue = (value, min, max) => {
  const numeric = Number.parseFloat(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return Math.min(max, Math.max(min, numeric));
};

const roundTrendMetric = (value) => {
  const numeric = Number.parseFloat(value);
  return Number.isFinite(numeric) ? Math.round(numeric * 10) / 10 : null;
};

const averageTrendMetrics = (values) => {
  const numericValues = (values || []).filter((value) => Number.isFinite(value));
  if (!numericValues.length) {
    return null;
  }
  return roundTrendMetric(numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length);
};

const computeExerciseFeedbackCompliance = (feedback) => {
  const ratios = [];
  [
    ['completed_sets', 'prescribed_sets'],
    ['completed_repetitions', 'prescribed_repetitions'],
    ['completed_time', 'prescribed_time'],
  ].forEach(([completedKey, prescribedKey]) => {
    const prescribed = Number.parseFloat(feedback?.[prescribedKey]);
    const completed = Number.parseFloat(feedback?.[completedKey]);
    if (Number.isFinite(prescribed) && prescribed > 0 && Number.isFinite(completed)) {
      ratios.push(Math.min(100, Math.max(0, (completed / prescribed) * 100)));
    }
  });

  if (!ratios.length) {
    return null;
  }

  return roundTrendMetric(ratios.reduce((sum, value) => sum + value, 0) / ratios.length);
};

const buildExerciseFeedbackCompletionSummary = (feedback) => {
  const segments = [];

  if (feedback?.completed_sets != null && feedback?.prescribed_sets != null) {
    segments.push(`${feedback.completed_sets}/${feedback.prescribed_sets} sets`);
  }
  if (feedback?.completed_repetitions != null && feedback?.prescribed_repetitions != null) {
    segments.push(`${feedback.completed_repetitions}/${feedback.prescribed_repetitions} reps`);
  }
  if (feedback?.completed_time != null && feedback?.prescribed_time != null) {
    segments.push(`${feedback.completed_time}/${feedback.prescribed_time}s`);
  }

  return segments.join(' | ');
};

const getTrendPainColor = (value) => {
  if (!Number.isFinite(value)) return '#b0bec5';
  if (value < 35) return '#2e7d32';
  if (value <= 55) return '#f9a825';
  return '#d84315';
};

const getTrendComplianceColor = (value) => {
  if (!Number.isFinite(value)) return '#b0bec5';
  if (value >= 85) return '#2e7d32';
  if (value >= 60) return '#f9a825';
  return '#c62828';
};

const buildPainTrendSummary = (values) => {
  const numericValues = (values || []).filter((value) => Number.isFinite(value));
  if (!numericValues.length) {
    return { direction: 'unknown', delta: null };
  }
  if (numericValues.length === 1) {
    return { direction: 'single', delta: 0 };
  }

  const delta = roundTrendMetric(numericValues[numericValues.length - 1] - numericValues[0]);
  if (!Number.isFinite(delta)) {
    return { direction: 'unknown', delta: null };
  }
  if (Math.abs(delta) < PAIN_TREND_STABLE_DELTA) {
    return { direction: 'stable', delta };
  }
  return {
    direction: delta > 0 ? 'increasing' : 'decreasing',
    delta,
  };
};

const formatPainTrendLabel = (trend) => {
  if (!trend || !Number.isFinite(trend.delta)) {
    return 'Pain trend unavailable';
  }
  if (trend.direction === 'single') {
    return 'Single pain reading';
  }
  if (trend.direction === 'stable') {
    return `Stable (${Math.abs(trend.delta)} pts)`;
  }
  return trend.direction === 'increasing'
    ? `Increasing by ${Math.abs(trend.delta)} pts`
    : `Decreasing by ${Math.abs(trend.delta)} pts`;
};

const getPainTrendAccent = (trend) => {
  if (!trend || !Number.isFinite(trend.delta)) return '#7a8ea5';
  if (trend.direction === 'increasing') return '#c62828';
  if (trend.direction === 'decreasing') return '#2e7d32';
  return '#5f7287';
};

const buildExerciseTrendSummaryFromSessions = (sessions, normalizeExerciseKey, getDifficultyDisplayLabel) => {
  if (!sessions.length) {
    return DEFAULT_EXERCISE_TREND_SUMMARY;
  }

  const trendSessions = sessions.map((session, index) => {
    const groupedFeedback = new Map();
    const completedAtRaw = session?.completed_date || null;
    const completedAt = completedAtRaw ? moment(completedAtRaw) : null;
    const sessionTime = completedAt && completedAt.isValid() ? completedAt.valueOf() : index;

    (session.exercise_feedback || []).forEach((feedback, feedbackIndex) => {
      const exerciseTitle = (feedback?.exercise_title || feedback?.exercise_id || 'Exercise').toString().trim();
      const exerciseKey = feedback?.exercise_id
        ? `id:${feedback.exercise_id}`
        : `title:${normalizeExerciseKey(exerciseTitle)}`;
      const painValue = clampTrendValue(feedback?.painLevel, 0, 100);
      const complianceValue = computeExerciseFeedbackCompliance(feedback);
      const completionSummary = buildExerciseFeedbackCompletionSummary(feedback);
      const groupedEntry = groupedFeedback.get(exerciseKey) || {
        exerciseKey,
        exerciseTitle,
        painValues: [],
        complianceValues: [],
        feedbackItems: [],
      };

      if (Number.isFinite(painValue)) {
        groupedEntry.painValues.push(painValue);
      }
      if (Number.isFinite(complianceValue)) {
        groupedEntry.complianceValues.push(complianceValue);
      }

      groupedEntry.feedbackItems.push({
        id: feedback?.id || `${session?.id || 'session'}-${feedbackIndex}`,
        exerciseTitle,
        painValue,
        complianceValue,
        difficultyLabel: feedback?.difficulty != null ? getDifficultyDisplayLabel(feedback.difficulty) : null,
        completionSummary,
        note: feedback?.note || '',
      });

      groupedFeedback.set(exerciseKey, groupedEntry);
    });

    const entries = Array.from(groupedFeedback.values())
      .map((entry) => ({
        exerciseKey: entry.exerciseKey,
        exerciseTitle: entry.exerciseTitle,
        averagePain: averageTrendMetrics(entry.painValues),
        averageCompliance: averageTrendMetrics(entry.complianceValues),
        feedbackItems: entry.feedbackItems,
      }))
      .sort((left, right) => left.exerciseTitle.localeCompare(right.exerciseTitle));

    return {
      sessionId: String(session?.id || `session-${index}`),
      label: completedAt && completedAt.isValid() ? completedAt.format('MMM D') : `S${index + 1}`,
      sublabel: completedAt && completedAt.isValid() ? completedAt.format('HH:mm') : 'Time n/a',
      fullLabel: completedAt && completedAt.isValid() ? completedAt.format('MMM D, YYYY [at] HH:mm') : 'Session date unavailable',
      dateKey: completedAt && completedAt.isValid() ? completedAt.format('YYYY-MM-DD') : `unknown-${index}`,
      dateLabel: completedAt && completedAt.isValid() ? completedAt.format('MMM D') : 'Date n/a',
      timestamp: sessionTime,
      entries,
      sessionFeedbackItems: Array.isArray(session?.session_feedback) ? session.session_feedback : [],
      averagePain: averageTrendMetrics(entries.map((entry) => entry.averagePain)),
      averageCompliance: averageTrendMetrics(entries.map((entry) => entry.averageCompliance)),
    };
  });

  const exercisesByKey = new Map();
  trendSessions.forEach((session) => {
    session.entries.forEach((entry) => {
      const existingExercise = exercisesByKey.get(entry.exerciseKey) || {
        exerciseKey: entry.exerciseKey,
        exerciseTitle: entry.exerciseTitle,
        painValues: [],
        complianceValues: [],
        painSeries: [],
        pointsBySessionId: {},
        latestTimestamp: 0,
      };

      if (Number.isFinite(entry.averagePain)) {
        existingExercise.painValues.push(entry.averagePain);
        existingExercise.painSeries.push(entry.averagePain);
      }
      if (Number.isFinite(entry.averageCompliance)) {
        existingExercise.complianceValues.push(entry.averageCompliance);
      }

      existingExercise.pointsBySessionId[session.sessionId] = entry;
      existingExercise.latestTimestamp = Math.max(existingExercise.latestTimestamp, session.timestamp || 0);
      exercisesByKey.set(entry.exerciseKey, existingExercise);
    });
  });

  const exercises = Array.from(exercisesByKey.values())
    .map((entry) => ({
      exerciseKey: entry.exerciseKey,
      exerciseTitle: entry.exerciseTitle,
      averagePain: averageTrendMetrics(entry.painValues),
      averageCompliance: averageTrendMetrics(entry.complianceValues),
      painTrend: buildPainTrendSummary(entry.painSeries),
      pointsBySessionId: entry.pointsBySessionId,
      latestTimestamp: entry.latestTimestamp,
      sessionCount: Object.keys(entry.pointsBySessionId).length,
    }))
    .sort((left, right) => {
      if (right.latestTimestamp !== left.latestTimestamp) {
        return right.latestTimestamp - left.latestTimestamp;
      }
      return left.exerciseTitle.localeCompare(right.exerciseTitle);
    });

  const sessionGroups = trendSessions.reduce((groups, session) => {
    const previousGroup = groups[groups.length - 1];
    if (previousGroup && previousGroup.dateKey === session.dateKey) {
      previousGroup.sessions.push(session);
      return groups;
    }
    groups.push({
      dateKey: session.dateKey,
      dateLabel: session.dateLabel,
      sessions: [session],
    });
    return groups;
  }, []);

  const overall = {
    averagePain: averageTrendMetrics(trendSessions.map((session) => session.averagePain)),
    averageCompliance: averageTrendMetrics(trendSessions.map((session) => session.averageCompliance)),
    painTrend: buildPainTrendSummary(trendSessions.map((session) => session.averagePain)),
  };

  return { sessions: trendSessions, sessionGroups, exercises, overall };
};

const ExerciseTrendDialog = ({
  open,
  onClose,
  sessions,
  assessmentDate,
  plannedSessionsPerDayOverall,
  normalizeExerciseKey,
  getDifficultyDisplayLabel,
}) => {
  const classes = useStyles();
  const exerciseTrendCacheRef = useRef({ source: null, value: DEFAULT_EXERCISE_TREND_SUMMARY });
  const [exerciseTrendLoading, setExerciseTrendLoading] = useState(false);
  const [exerciseTrendSummary, setExerciseTrendSummary] = useState(DEFAULT_EXERCISE_TREND_SUMMARY);
  const [exerciseTrendLastResolvedSummary, setExerciseTrendLastResolvedSummary] = useState(DEFAULT_EXERCISE_TREND_SUMMARY);
  const [exerciseTrendSessionLimit, setExerciseTrendSessionLimit] = useState(EXERCISE_TREND_SESSION_LIMIT);

  const exerciseTrendSourceSessions = useMemo(() => (
    (sessions || []).filter((session) => (
      !session?.is_patient_scope
      && Array.isArray(session?.exercise_feedback)
      && session.exercise_feedback.length > 0
    ))
  ), [sessions]);

  const exerciseTrendAvailableSessions = useMemo(() => (
    exerciseTrendSourceSessions.slice(0, exerciseTrendSessionLimit).reverse()
  ), [exerciseTrendSessionLimit, exerciseTrendSourceSessions]);

  const completedSessionCount = useMemo(() => (
    (sessions || []).filter((session) => session && !session?.is_patient_scope).length
  ), [sessions]);

  const treatmentStartTime = useMemo(() => {
    const assessmentDateIso = assessmentDate && (assessmentDate.assessment_date || assessmentDate.date || assessmentDate.assessmentDate || assessmentDate);
    const parsedAssessmentDate = assessmentDateIso ? Date.parse(assessmentDateIso) : Number.NaN;
    if (Number.isFinite(parsedAssessmentDate)) {
      return parsedAssessmentDate;
    }

    const earliestSessionTime = (sessions || []).reduce((earliest, session) => {
      if (session?.is_patient_scope) {
        return earliest;
      }
      const completedAt = Date.parse(session?.completed_date || '');
      if (!Number.isFinite(completedAt)) {
        return earliest;
      }
      if (!Number.isFinite(earliest)) {
        return completedAt;
      }
      return Math.min(earliest, completedAt);
    }, Number.NaN);

    return Number.isFinite(earliestSessionTime) ? earliestSessionTime : null;
  }, [assessmentDate, sessions]);

  const overallComplianceSinceStart = useMemo(() => {
    if (!Number.isFinite(plannedSessionsPerDayOverall) || plannedSessionsPerDayOverall <= 0 || !Number.isFinite(treatmentStartTime)) {
      return null;
    }

    const elapsedDays = Math.max(1, (Date.now() - treatmentStartTime) / (1000 * 60 * 60 * 24));
    const avgSessionsPerDay = completedSessionCount / elapsedDays;
    return roundTrendMetric((avgSessionsPerDay / plannedSessionsPerDayOverall) * 100);
  }, [completedSessionCount, plannedSessionsPerDayOverall, treatmentStartTime]);

  const exerciseTrendHasOlderHistory = exerciseTrendSessionLimit < exerciseTrendSourceSessions.length;
  const exerciseTrendHasNewerHistory = exerciseTrendSessionLimit > EXERCISE_TREND_SESSION_LIMIT;
  const exerciseTrendVisibleSummary = exerciseTrendLoading && exerciseTrendLastResolvedSummary.sessions.length > 0
    ? exerciseTrendLastResolvedSummary
    : exerciseTrendSummary;

  useEffect(() => {
    setExerciseTrendSessionLimit(EXERCISE_TREND_SESSION_LIMIT);
    setExerciseTrendSummary(DEFAULT_EXERCISE_TREND_SUMMARY);
    setExerciseTrendLastResolvedSummary(DEFAULT_EXERCISE_TREND_SUMMARY);
    setExerciseTrendLoading(false);
    exerciseTrendCacheRef.current = { source: null, value: DEFAULT_EXERCISE_TREND_SUMMARY };
  }, [sessions, assessmentDate, plannedSessionsPerDayOverall]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    if (!exerciseTrendAvailableSessions.length) {
      setExerciseTrendSummary(DEFAULT_EXERCISE_TREND_SUMMARY);
      setExerciseTrendLastResolvedSummary(DEFAULT_EXERCISE_TREND_SUMMARY);
      setExerciseTrendLoading(false);
      return undefined;
    }

    if (exerciseTrendCacheRef.current.source === exerciseTrendAvailableSessions) {
      setExerciseTrendSummary(exerciseTrendCacheRef.current.value);
      setExerciseTrendLastResolvedSummary(exerciseTrendCacheRef.current.value);
      setExerciseTrendLoading(false);
      return undefined;
    }

    let isCancelled = false;
    setExerciseTrendLoading(true);

    const timerId = window.setTimeout(() => {
      const nextSummary = buildExerciseTrendSummaryFromSessions(
        exerciseTrendAvailableSessions,
        normalizeExerciseKey,
        getDifficultyDisplayLabel,
      );
      if (isCancelled) {
        return;
      }
      exerciseTrendCacheRef.current = {
        source: exerciseTrendAvailableSessions,
        value: nextSummary,
      };
      setExerciseTrendSummary(nextSummary);
      setExerciseTrendLastResolvedSummary(nextSummary);
      setExerciseTrendLoading(false);
    }, 0);

    return () => {
      isCancelled = true;
      window.clearTimeout(timerId);
    };
  }, [open, exerciseTrendAvailableSessions, normalizeExerciseKey, getDifficultyDisplayLabel]);

  const renderPainTrendIndicator = (trend) => {
    let IconComponent = TrendingFlatIcon;
    let accentColor = getPainTrendAccent(trend);

    if (trend?.direction === 'increasing') {
      IconComponent = TrendingUpIcon;
    } else if (trend?.direction === 'decreasing') {
      IconComponent = TrendingDownIcon;
    }

    return (
      <Box display="flex" alignItems="center" style={{ gap: 6, color: accentColor }}>
        <IconComponent style={{ fontSize: 'clamp(0.9rem, 0.96vw, 1.1rem)' }} />
        <Typography component="span" style={{ color: accentColor, fontWeight: 700, fontSize: 'inherit' }}>
          {formatPainTrendLabel(trend)}
        </Typography>
      </Box>
    );
  };

  const renderExerciseTrendTooltip = ({ title, subtitle = null, exerciseEntries = [], sessionFeedbackItems = [] }) => (
    <Box style={{ maxWidth: 340, padding: 'clamp(2px, 0.2vw, 4px) 0' }}>
      <Typography style={{ fontWeight: 700, fontSize: 'clamp(0.7rem, 0.76vw, 0.86rem)', color: '#002D55' }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography style={{ fontSize: 'clamp(0.64rem, 0.68vw, 0.78rem)', color: '#5f7287', marginTop: 2, marginBottom: 6 }}>
          {subtitle}
        </Typography>
      ) : null}
      {exerciseEntries.length > 0 ? (
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {exerciseEntries.map((entry) => (
            <Box key={`${title}-${entry.exerciseKey}`} style={{ borderTop: '1px solid rgba(0,45,85,0.08)', paddingTop: 6 }}>
              <Typography style={{ fontWeight: 700, fontSize: 'clamp(0.64rem, 0.68vw, 0.78rem)', color: '#002D55' }}>
                {entry.exerciseTitle}
              </Typography>
              {(entry.feedbackItems || []).map((item) => (
                <Typography key={item.id} style={{ fontSize: 'clamp(0.62rem, 0.66vw, 0.76rem)', color: '#486274', lineHeight: 1.45 }}>
                  {[
                    Number.isFinite(item.complianceValue) ? `Completion ${item.complianceValue}%` : null,
                    Number.isFinite(item.painValue) ? `Pain ${item.painValue}/100` : null,
                    item.difficultyLabel ? `Difficulty ${item.difficultyLabel}` : null,
                    item.completionSummary || null,
                    item.note || null,
                  ].filter(Boolean).join(' | ')}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      ) : (
        <Typography style={{ fontSize: 'clamp(0.62rem, 0.66vw, 0.76rem)', color: '#5f7287', lineHeight: 1.45 }}>
          No exercise feedback recorded for this session.
        </Typography>
      )}
      {sessionFeedbackItems.length > 0 ? (
        <Box style={{ marginTop: 8, borderTop: '1px solid rgba(0,45,85,0.08)', paddingTop: 6 }}>
          <Typography style={{ fontWeight: 700, fontSize: 'clamp(0.64rem, 0.68vw, 0.78rem)', color: '#002D55' }}>
            Session feedback
          </Typography>
          {sessionFeedbackItems.map((item, index) => (
            <Typography key={`${title}-session-feedback-${item.id || index}`} style={{ fontSize: 'clamp(0.62rem, 0.66vw, 0.76rem)', color: '#486274', lineHeight: 1.45 }}>
              {[
                item?.like_status ? (item.like_status === 'like' ? 'Liked' : item.like_status) : null,
                item?.feedback || null,
              ].filter(Boolean).join(' | ')}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      keepMounted
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle style={{ padding: '18px 24px 10px 24px', background: '#f7faff' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 12 }}>
          <Box>
            <Typography style={{ color: '#002D55', fontWeight: 800, fontSize: 'clamp(1rem, 1.1vw, 1.2rem)' }}>
              Exercise feedback trends
            </Typography>
            <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.68rem, 0.72vw, 0.84rem)', marginTop: 4 }}>
              Showing {exerciseTrendAvailableSessions.length} of {exerciseTrendSourceSessions.length} completed sessions with exercise completion and pain trends.
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon style={{ color: '#64748B' }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ padding: '16px 24px 20px 24px', background: '#f7faff' }}>
        {exerciseTrendLoading && exerciseTrendLastResolvedSummary.sessions.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" style={{ minHeight: 280, gap: 12 }}>
            <CircularProgress size={34} thickness={4.4} style={{ color: '#002D55' }} />
            <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)' }}>
              Loading exercise trends...
            </Typography>
          </Box>
        ) : exerciseTrendVisibleSummary.sessions.length === 0 ? (
          <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)' }}>
            No completed exercise feedback is available yet.
          </Typography>
        ) : (
          <Box style={{ position: 'relative' }}>
            {exerciseTrendLoading ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  borderRadius: 18,
                  background: 'rgba(247,250,255,0.62)',
                  gap: 12,
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    minWidth: 220,
                    padding: '16px 20px',
                    borderRadius: 16,
                    background: 'rgba(255,255,255,0.94)',
                    border: '1px solid rgba(0,45,85,0.1)',
                    boxShadow: '0 10px 26px rgba(0,45,85,0.12)',
                    gap: 12,
                  }}
                >
                  <CircularProgress size={34} thickness={4.4} style={{ color: '#002D55' }} />
                  <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)' }}>
                    Loading exercise trends...
                  </Typography>
                </Box>
              </Box>
            ) : null}
            <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
              <Box style={{ borderRadius: 16, border: '1px solid rgba(0,45,85,0.08)', background: '#ffffff', boxShadow: '0 6px 18px rgba(0,45,85,0.06)', padding: '14px 16px' }}>
                <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.78rem, 0.85vw, 0.94rem)', marginBottom: 8 }}>
                  Overall trend
                </Typography>
                <Box display="flex" alignItems="stretch" justifyContent="space-between" style={{ gap: 12, flexWrap: 'wrap' }}>
                  <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 66 }}>
                    <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.62rem, 0.66vw, 0.76rem)' }}>
                      Avg pain across latest sessions
                    </Typography>
                    <Typography style={{ color: '#002D55', fontWeight: 800, fontSize: 'clamp(1rem, 1.08vw, 1.16rem)', marginTop: 2 }}>
                      {Number.isFinite(exerciseTrendVisibleSummary.overall?.averagePain) ? `${exerciseTrendVisibleSummary.overall.averagePain}/100` : 'No pain data'}
                    </Typography>
                    <Box style={{ minHeight: 16, marginTop: 2 }} />
                  </Box>
                  <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 66 }}>
                    <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.62rem, 0.66vw, 0.76rem)' }}>
                      Avg. Pain per exercise direction
                    </Typography>
                    <Box style={{ marginTop: 2, fontSize: 'clamp(0.72rem, 0.78vw, 0.88rem)' }}>
                      {renderPainTrendIndicator(exerciseTrendVisibleSummary.overall?.painTrend)}
                    </Box>
                    <Box style={{ minHeight: 16, marginTop: 2 }} />
                  </Box>
                  <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 66 }}>
                    <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.62rem, 0.66vw, 0.76rem)' }}>
                      Overall compliance since start
                    </Typography>
                    <Typography style={{ color: '#002D55', fontWeight: 800, fontSize: 'clamp(1rem, 1.08vw, 1.16rem)', marginTop: 2 }}>
                      {Number.isFinite(overallComplianceSinceStart) ? `${overallComplianceSinceStart}%` : 'No data'}
                    </Typography>
                    <Typography style={{ color: '#7a8ea5', fontSize: 'clamp(0.58rem, 0.62vw, 0.7rem)', marginTop: 2 }}>
                      {Number.isFinite(plannedSessionsPerDayOverall) ? `${plannedSessionsPerDayOverall} planned/day` : 'Plan unavailable'}
                    </Typography>
                  </Box>
                  <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 66 }}>
                    <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.62rem, 0.66vw, 0.76rem)' }}>
                      Avg completion per exercise
                    </Typography>
                    <Typography style={{ color: '#002D55', fontWeight: 800, fontSize: 'clamp(1rem, 1.08vw, 1.16rem)', marginTop: 2 }}>
                      {Number.isFinite(exerciseTrendVisibleSummary.overall?.averageCompliance) ? `${exerciseTrendVisibleSummary.overall.averageCompliance}%` : 'No data'}
                    </Typography>
                    <Box style={{ minHeight: 16, marginTop: 2 }} />
                  </Box>
                </Box>
              </Box>
              <Box style={{ borderRadius: 16, border: '1px solid rgba(0,45,85,0.08)', background: '#ffffff', boxShadow: '0 6px 18px rgba(0,45,85,0.06)', padding: '14px 16px' }}>
                <Box display="flex" alignItems="center" justifyContent="flex-start" style={{ gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  <Tooltip title="Show older sessions" arrow>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setExerciseTrendSessionLimit((currentLimit) => Math.min(currentLimit + EXERCISE_TREND_SESSION_STEP, exerciseTrendSourceSessions.length))}
                        disabled={!exerciseTrendHasOlderHistory || exerciseTrendLoading}
                        style={{
                          padding: 4,
                          color: exerciseTrendHasOlderHistory ? '#002D55' : '#9aa8b5',
                          backgroundColor: '#ffffff',
                          border: '1px solid rgba(0,45,85,0.12)',
                          borderRadius: 10,
                        }}
                      >
                        <ChevronLeftIcon style={{ fontSize: 'clamp(18px, 1.45vw, 22px)' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.78rem, 0.85vw, 0.94rem)' }}>
                    Session timeline
                  </Typography>
                  <Tooltip title="Show newer sessions" arrow>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setExerciseTrendSessionLimit((currentLimit) => Math.max(EXERCISE_TREND_SESSION_LIMIT, currentLimit - EXERCISE_TREND_SESSION_STEP))}
                        disabled={!exerciseTrendHasNewerHistory || exerciseTrendLoading}
                        style={{
                          padding: 4,
                          color: exerciseTrendHasNewerHistory ? '#002D55' : '#9aa8b5',
                          backgroundColor: '#ffffff',
                          border: '1px solid rgba(0,45,85,0.12)',
                          borderRadius: 10,
                        }}
                      >
                        <ChevronRightIcon style={{ fontSize: 'clamp(18px, 1.45vw, 22px)' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                <Box display="flex" alignItems="stretch" style={{ gap: 12, overflowX: 'auto', paddingBottom: 4, flexWrap: 'nowrap' }}>
                  {exerciseTrendVisibleSummary.sessionGroups.map((group) => (
                    <Box
                      key={`trend-group-${group.dateKey}`}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        flex: '0 0 auto',
                        minWidth: Math.max(96, group.sessions.length * 64),
                        padding: '10px 12px',
                        borderRadius: 16,
                        background: group.sessions.length > 1 ? '#eef5fb' : 'rgba(247,250,255,0.7)',
                        border: group.sessions.length > 1 ? '1px solid #dce8f5' : '1px solid rgba(220,232,245,0.5)',
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 8, flexWrap: 'nowrap' }}>
                        <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.64rem, 0.68vw, 0.78rem)' }}>
                          {group.dateLabel}
                        </Typography>
                        {group.sessions.length > 1 ? (
                          <Chip
                            size="small"
                            label={`${group.sessions.length} sessions`}
                            style={{ backgroundColor: '#fff3e8', color: '#fb741a', fontWeight: 700, height: 22 }}
                          />
                        ) : null}
                      </Box>
                      <Box display="flex" alignItems="center" style={{ gap: 10, flexWrap: 'nowrap' }}>
                        {group.sessions.map((session) => (
                          <Tooltip
                            key={`trend-session-${session.sessionId}`}
                            arrow
                            interactive
                            placement="top"
                            classes={{ tooltip: classes.trendTooltip, arrow: classes.trendTooltipArrow }}
                            title={renderExerciseTrendTooltip({
                              title: session.fullLabel,
                              subtitle: `${session.entries.length} exercise${session.entries.length === 1 ? '' : 's'} logged`,
                              exerciseEntries: session.entries,
                              sessionFeedbackItems: session.sessionFeedbackItems,
                            })}
                          >
                            <Box display="flex" flexDirection="column" alignItems="center" style={{ minWidth: 54, gap: 6, flex: '0 0 auto' }}>
                              <Box
                                style={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  backgroundColor: getTrendPainColor(session.averagePain),
                                  border: `2px solid ${getTrendComplianceColor(session.averageCompliance)}`,
                                  boxShadow: '0 0 0 3px rgba(0,45,85,0.08)',
                                }}
                              />
                              <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.58rem, 0.62vw, 0.7rem)' }}>
                                {session.sublabel}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box style={{ borderRadius: 16, border: '1px solid rgba(0,45,85,0.08)', background: '#ffffff', boxShadow: '0 6px 18px rgba(0,45,85,0.06)', overflow: 'hidden' }}>
                <Box style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1.35fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(220px, 1.6fr)', gap: 12, padding: '12px 16px', background: '#f7faff', borderBottom: '1px solid rgba(0,45,85,0.08)' }}>
                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.68rem, 0.72vw, 0.82rem)' }}>Exercise</Typography>
                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.68rem, 0.72vw, 0.82rem)' }}>Avg completion</Typography>
                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.68rem, 0.72vw, 0.82rem)' }}>Avg pain</Typography>
                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.68rem, 0.72vw, 0.82rem)' }}>Latest sessions</Typography>
                </Box>
                {exerciseTrendVisibleSummary.exercises.map((exercise, exerciseIndex) => (
                  <Box
                    key={`exercise-trend-row-${exercise.exerciseKey}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(180px, 1.35fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(220px, 1.6fr)',
                      gap: 12,
                      padding: '14px 16px',
                      borderBottom: exerciseIndex === exerciseTrendVisibleSummary.exercises.length - 1 ? 'none' : '1px solid rgba(207,216,220,0.72)',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)' }}>
                        {exercise.exerciseTitle}
                      </Typography>
                      <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.62rem, 0.66vw, 0.76rem)', marginTop: 4 }}>
                        {exercise.sessionCount} session{exercise.sessionCount === 1 ? '' : 's'} with feedback
                      </Typography>
                      <Box style={{ marginTop: 4, fontSize: 'clamp(0.62rem, 0.66vw, 0.76rem)' }}>
                        {renderPainTrendIndicator(exercise.painTrend)}
                      </Box>
                    </Box>
                    <Box>
                      <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.66rem, 0.7vw, 0.8rem)', marginBottom: 6 }}>
                        {Number.isFinite(exercise.averageCompliance) ? `${exercise.averageCompliance}%` : 'No data'}
                      </Typography>
                      <Box style={{ width: '100%', height: 8, borderRadius: 999, background: '#e6eaf0', overflow: 'hidden' }}>
                        <Box
                          style={{
                            width: `${Number.isFinite(exercise.averageCompliance) ? exercise.averageCompliance : 0}%`,
                            height: '100%',
                            borderRadius: 999,
                            background: getTrendComplianceColor(exercise.averageCompliance),
                          }}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.66rem, 0.7vw, 0.8rem)', marginBottom: 6 }}>
                        {Number.isFinite(exercise.averagePain) ? `${exercise.averagePain}/100` : 'No data'}
                      </Typography>
                      <Box style={{ width: '100%', height: 8, borderRadius: 999, background: '#e6eaf0', overflow: 'hidden' }}>
                        <Box
                          style={{
                            width: `${Number.isFinite(exercise.averagePain) ? exercise.averagePain : 0}%`,
                            height: '100%',
                            borderRadius: 999,
                            background: getTrendPainColor(exercise.averagePain),
                          }}
                        />
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" style={{ gap: 10, overflowX: 'auto', paddingBottom: 2 }}>
                      {exerciseTrendVisibleSummary.sessionGroups.map((group, groupIndex) => (
                        <React.Fragment key={`exercise-trend-group-${exercise.exerciseKey}-${group.dateKey}`}>
                          {groupIndex > 0 ? (
                            <Box style={{ flex: '0 0 18px', height: 2, background: 'rgba(0,45,85,0.08)' }} />
                          ) : null}
                          <Box
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 8px',
                              borderRadius: 12,
                              background: group.sessions.length > 1 ? '#eef5fb' : 'transparent',
                              border: group.sessions.length > 1 ? '1px solid #dce8f5' : '1px solid transparent',
                            }}
                          >
                            {group.sessions.map((session, sessionIndex) => {
                              const point = exercise.pointsBySessionId[session.sessionId] || null;
                              return (
                                <React.Fragment key={`exercise-trend-point-${exercise.exerciseKey}-${session.sessionId}`}>
                                  {sessionIndex > 0 ? (
                                    <Box style={{ flex: '0 0 14px', height: 2, background: 'rgba(0,45,85,0.08)' }} />
                                  ) : null}
                                  <Tooltip
                                    arrow
                                    interactive
                                    placement="top"
                                    classes={{ tooltip: classes.trendTooltip, arrow: classes.trendTooltipArrow }}
                                    title={renderExerciseTrendTooltip({
                                      title: session.fullLabel,
                                      subtitle: point ? exercise.exerciseTitle : 'No feedback for this exercise',
                                      exerciseEntries: point ? [{
                                        exerciseKey: exercise.exerciseKey,
                                        exerciseTitle: exercise.exerciseTitle,
                                        feedbackItems: point.feedbackItems,
                                      }] : [],
                                    })}
                                  >
                                    <Box
                                      style={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: point ? getTrendPainColor(point.averagePain) : '#ffffff',
                                        border: `2px solid ${point ? getTrendComplianceColor(point.averageCompliance) : 'rgba(0,45,85,0.14)'}`,
                                        boxShadow: point ? '0 0 0 2px rgba(0,45,85,0.06)' : 'none',
                                        opacity: point ? 1 : 0.55,
                                        flex: '0 0 auto',
                                      }}
                                    />
                                  </Tooltip>
                                </React.Fragment>
                              );
                            })}
                          </Box>
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: 16, gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eeeeee', backgroundColor: '#ffffff' }}>
        <Button onClick={onClose} variant="outlined" className={classes.footerButton}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ExerciseTrendDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sessions: PropTypes.array,
  assessmentDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  plannedSessionsPerDayOverall: PropTypes.number,
  normalizeExerciseKey: PropTypes.func,
  getDifficultyDisplayLabel: PropTypes.func,
};

ExerciseTrendDialog.defaultProps = {
  sessions: [],
  assessmentDate: null,
  plannedSessionsPerDayOverall: null,
  normalizeExerciseKey: defaultNormalizeExerciseKey,
  getDifficultyDisplayLabel: defaultGetDifficultyDisplayLabel,
};

export default ExerciseTrendDialog;
